import { fetchClient } from "@/api/api";
import type {
	createRelationInput,
	createRelationOutput,
	decideRelationInput,
	decideRelationOutput,
	getAggregatedSellerInput,
	getAggregatedSellerOutput,
	getRelationInput,
	getRelationOutput,
	getRelationTransactionsInput,
	getRelationTransactionsOutput,
	getRelationsInput,
	getRelationsOutput,
	updateRelationInput,
	updateRelationOutput,
} from "@/api/custom_types";
import { useApiError, useTranslation } from "@/hooks";
import { currentUserAtom } from "@/store/login";
import { normalizeEmptyStringsToNull } from "@/utils/normalize";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { toast } from "@st-marketplace/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { RELATIONS_SERIVCE_KEYS } from "./keys";

type GetRelationResponseTransformed = getRelationOutput & {
	is_seller: boolean;
	is_buyer: boolean;
};

const useGetRelations: CustomQueryHook<getRelationsOutput, getRelationsInput> =
	(payload, options) => {
		return useQuery({
			...options,
			queryKey: RELATIONS_SERIVCE_KEYS.useGetRelations(payload),
			queryFn: async () => {
				const { data, error } = await fetchClient.GET(
					"/v1/marketplace/relations/",
					{ params: { query: payload?.query }, throwOnError: true },
				);
				if (error) throw error;
				return data;
			},
		});
	};

const useGetAggregatedSellers: CustomQueryHook<
	getAggregatedSellerOutput,
	getAggregatedSellerInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: RELATIONS_SERIVCE_KEYS.useGetAggregatedSellers(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/relations/sellers/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useRelation: CustomQueryHook<
	getRelationOutput,
	getRelationInput,
	GetRelationResponseTransformed
> = (payload, options) => {
	const currentUser = useAtomValue(currentUserAtom);

	return useQuery({
		...options,
		queryKey: RELATIONS_SERIVCE_KEYS.useRelation(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/relations/{relation_id}",
				{ params: { path: payload.path }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		select: (data) => {
			return {
				...data,
				is_buyer: data.buyer_id === currentUser?.id,
				is_seller: data.seller_id === currentUser?.id,
			};
		},
	});
};

const useRelationTransactions: CustomQueryHook<
	getRelationTransactionsOutput,
	getRelationTransactionsInput
> = (payload, options) =>
	useQuery({
		...options,
		queryKey: RELATIONS_SERIVCE_KEYS.useRelationTransactions(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/relations/{relation_id}/transactions",
				{ params: payload, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});

const useCreateRelation: CustomMutationHook<
	createRelationInput,
	createRelationOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: RELATIONS_SERIVCE_KEYS.useCreateRelation(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/relations/",
				{
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			toast.success(t("relation.addRequest.successSent"));
			queryClient.invalidateQueries({
				queryKey: RELATIONS_SERIVCE_KEYS.useGetRelations(),
			});
			queryClient.invalidateQueries({
				queryKey: RELATIONS_SERIVCE_KEYS.useGetAggregatedSellers(),
			});
		},
		onError: toastApiError,
	});
};

const useDecideRelation: CustomMutationHook<
	decideRelationInput,
	decideRelationOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: RELATIONS_SERIVCE_KEYS.useDecideRelation(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PUT(
				"/v1/marketplace/relations/{relation_id}",
				{
					params: { path: payload.path },
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: RELATIONS_SERIVCE_KEYS.useGetRelations(),
			});
			await queryClient.invalidateQueries({
				queryKey: RELATIONS_SERIVCE_KEYS.useGetAggregatedSellers(),
			});
		},
		onError: toastApiError,
	});
};

const useUpdateRelation: CustomMutationHook<
	updateRelationInput,
	updateRelationOutput
> = (options) => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: RELATIONS_SERIVCE_KEYS.useUpdateRelation(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/relations/{relation_id}",
				{
					params: { path: payload.path },
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("relations.edit.formSuccessMessage"));
			queryClient.invalidateQueries({
				queryKey: RELATIONS_SERIVCE_KEYS.useGetRelations(),
			});
			queryClient.invalidateQueries({
				queryKey: RELATIONS_SERIVCE_KEYS.useGetAggregatedSellers(),
			});
		},
		...options,
	});
};

export {
	useGetRelations,
	useGetAggregatedSellers,
	useRelation,
	useRelationTransactions,
	useCreateRelation,
	useDecideRelation,
	useUpdateRelation,
};
